import React, { Fragment, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useOktaAuth } from "@okta/okta-react";
import {
  Box,
  Heading,
  Text,
  Cluster,
  Stack,
  ButtonWithAction,
  constants,
} from "@thecb/components";
import connectUserPermissions from "@/components/currentUser/CurrentUserPermissions";
import { TableBody, Table, TableHeaderRow } from "@/components/generic/Table";
import { TableRow, TableRowEntry } from "../generic/NewTable/NewTable";
import { SEMI_BOLD } from "@/constants/fonts";
import { BORDER_PRIMARY, TEXT_PRIMARY } from "@/constants/colors";
import AlertBar from "@/components/alert-bar";
import {
  PageContainer,
  PageContent,
  ScrollArea,
} from "@/components/generic/PageLayout";

const UserRow =
  ({ oktaAuth, requestClient }) =>
  ({ slug, id, brand }) => {
    const handleClick = async () => {
      // Results in populating `currentUser` in Redux state.
      // When that occurs, the component transitions to the
      // dashboard page and loads the Periscope dashboard.
      await requestClient({ oktaAuth, client: slug });
    };

    return (
      <TableRow key={id} onClick={handleClick}>
        <TableRowEntry>
          <Text variant="pS">{brand}</Text>
        </TableRowEntry>
        <TableRowEntry>
          <Cluster justify="flex-end" align="center">
            <Text
              variant="pS"
              color={constants.colors.LUCKY_POINT}
              extraStyles={`cursor: pointer;`}
            >
              View Client
            </Text>
          </Cluster>
        </TableRowEntry>
      </TableRow>
    );
  };

const ClientsTable = ({
  alertBar,
  alertBarActions,
  clients,
  currentUser,
  requestClient,
  setClientAdminForm,
}) => {
  const history = useHistory();
  const { oktaAuth } = useOktaAuth();

  // Successfully loading the Periscope dashboard after clicking on a client
  // requires the `currentUser` values to be loaded in advance of navigating
  // to the dashboard page. Otherwise, RevM requests the dashboard using empty
  // API call parameters that results in a failure to fetch the populating data.
  useEffect(() => {
    const hasFetchedClient = currentUser?.client?.value?.slug?.length > 0;
    const hasFetchedAgencies = currentUser?.agencies?.value?.length > 0;

    if (hasFetchedClient && hasFetchedAgencies) {
      history.push("/dashboard");
    }
  }, [currentUser]);

  return (
    <Fragment>
      <Box padding="1rem 0 2rem" width="100%;">
        <PageContainer>
          <PageContent>
            {alertBar.alerts.length > 0 && (
              <Box padding="1rem 0">
                <AlertBar {...alertBar} {...alertBarActions} />
              </Box>
            )}
            <Stack>
              <Cluster justify="space-between" align="center">
                <Heading fontSize="1.75rem" weight={SEMI_BOLD} color={TEXT_PRIMARY}>
                  Clients
                </Heading>
                <ButtonWithAction
                  variant="primary"
                  text="Create new client admin"
                  action={setClientAdminForm}
                  isLoading={false}
                  extraStyles={`padding: 0.75rem 1.5rem; border-radius: 4px;`}
                />
              </Cluster>
              <Box
                padding="0"
                width="auto"
                boxShadow={`0px 0px 5px ${BORDER_PRIMARY}`}
                borderRadius="4px"
              >
                <ScrollArea>
                  <Table>
                    <TableBody>
                      <TableHeaderRow>
                        <th>
                          <Text weight="600" variant="pS">
                            Client name
                          </Text>
                        </th>
                        <th>
                          <Text weight="600" variant="pS">
                            Action
                          </Text>
                        </th>
                      </TableHeaderRow>
                      {clients.map(
                        UserRow({
                          oktaAuth,
                          requestClient,
                        })
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </Box>
            </Stack>
          </PageContent>
        </PageContainer>
      </Box>
    </Fragment>
  );
};

export default connectUserPermissions(ClientsTable);
